import { Injectable } from '@angular/core';
import dayjs, { Dayjs } from 'dayjs';

// 👉 Plugins
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import minMax from 'dayjs/plugin/minMax';
import duration, { Duration } from 'dayjs/plugin/duration';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

// Extend once globally
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(advancedFormat);
dayjs.extend(duration);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(minMax);

@Injectable({
  providedIn: 'root',
})
export class DateService {
  /**
   * Get current date/time
   */
  now(): Dayjs {
    return dayjs();
  }

  /**
   * Parse date (supports string, JS Date, Dayjs)
   */
  parse(date: string | Date | Dayjs, format?: string): Dayjs {
    return format ? dayjs(date, format) : dayjs(date);
  }

  /**
   * Format date
   */
  format(
    date: string | Date | Dayjs,
    pattern: string = 'YYYY-MM-DD HH:mm:ss',
  ): string {
    return dayjs(date).format(pattern);
  }

  /**
   * Convert to UTC
   */
  toUtc(date: string | Date | Dayjs): Dayjs {
    return dayjs(date).utc();
  }

  /**
   * Convert to Timezone
   */
  toTimezone(date: string | Date | Dayjs, tz: string): Dayjs {
    return dayjs(date).tz(tz);
  }

  /**
   * Add / Subtract
   */
  add(
    date: string | Date | Dayjs,
    amount: number,
    unit: dayjs.ManipulateType,
  ): Dayjs {
    return dayjs(date).add(amount, unit);
  }

  subtract(
    date: string | Date | Dayjs,
    amount: number,
    unit: dayjs.ManipulateType,
  ): Dayjs {
    return dayjs(date).subtract(amount, unit);
  }

  /**
   * Diff
   */
  diff(
    date1: string | Date | Dayjs,
    date2: string | Date | Dayjs,
    unit: dayjs.OpUnitType = 'millisecond',
    precise: boolean = false,
  ): number {
    return dayjs(date1).diff(dayjs(date2), unit, precise);
  }

  /**
   * Compare
   */
  isBefore(
    date1: string | Date | Dayjs,
    date2: string | Date | Dayjs,
    unit?: dayjs.OpUnitType,
  ): boolean {
    return dayjs(date1).isBefore(dayjs(date2), unit);
  }

  isAfter(
    date1: string | Date | Dayjs,
    date2: string | Date | Dayjs,
    unit?: dayjs.OpUnitType,
  ): boolean {
    return dayjs(date1).isAfter(dayjs(date2), unit);
  }

  isSame(
    date1: string | Date | Dayjs,
    date2: string | Date | Dayjs,
    unit?: dayjs.OpUnitType,
  ): boolean {
    return dayjs(date1).isSame(dayjs(date2), unit);
  }

  isSameOrAfter(
    date1: string | Date | Dayjs,
    date2: string | Date | Dayjs,
    unit?: dayjs.OpUnitType,
  ): boolean {
    return dayjs(date1).isSameOrAfter(dayjs(date2), unit);
  }

  isSameOrBefore(
    date1: string | Date | Dayjs,
    date2: string | Date | Dayjs,
    unit?: dayjs.OpUnitType,
  ): boolean {
    return dayjs(date1).isSameOrBefore(dayjs(date2), unit);
  }

  /**
   * Start / End of
   */
  startOf(date: string | Date | Dayjs, unit: dayjs.OpUnitType): Dayjs {
    return dayjs(date).startOf(unit);
  }

  endOf(date: string | Date | Dayjs, unit: dayjs.OpUnitType): Dayjs {
    return dayjs(date).endOf(unit);
  }

  /**
   * Relative Time ("2 days ago", "in 3 hours")
   */
  fromNow(date: string | Date | Dayjs): string {
    return dayjs(date).fromNow();
  }

  from(date: string | Date | Dayjs, comparedTo: string | Date | Dayjs): string {
    return dayjs(date).from(dayjs(comparedTo));
  }

  humanizeDuration(ms: number): string {
    return dayjs.duration(ms).humanize();
  }

  /**
   * Week / ISO week
   */
  weekOfYear(date: string | Date | Dayjs): number {
    return dayjs(date).week();
  }

  isoWeek(date: string | Date | Dayjs): number {
    return dayjs(date).isoWeek();
  }

  /**
   * Validation
   */
  isValid(date: any): boolean {
    return dayjs(date).isValid();
  }

  /**
   * Min / Max
   */
  min(dates: (string | Date | Dayjs)[]): Dayjs | null {
    return dayjs.min(dates.map((d) => dayjs(d)));
  }

  max(dates: (string | Date | Dayjs)[]): Dayjs | null {
    return dayjs.max(dates.map((d) => dayjs(d)));
  }

  dayjsToNgb(date: Dayjs): NgbDateStruct {
    return {
      year: date.year(),
      month: date.month() + 1, // dayjs months are 0-based
      day: date.date(),
    };
  }

  ngbToDayjs(date: NgbDateStruct | null): Dayjs | null | string {
    if (!date) return null;
    return dayjs(new Date(date.year, date.month - 1, date.day)).format(
      'YYYY-MM-DD',
    );
  }

  convertDDMMYYYYtoYYYYMMDD(dateStr: string): string | null {
    if (!dateStr) return null;

    const parsed = dayjs(dateStr, 'DD-MM-YYYY', true); // strict parsing

    if (!parsed.isValid()) return null;

    return parsed.format('YYYY-MM-DD');
  }

  formatToISO(date: string | Date | null | any): string | null {
    if (!date) return null;

    const d = dayjs(date);
    return d.isValid() ? d.format('YYYY-MM-DDTHH:mm:ss') : null;
  }

  formatDate(date: string): string {
    const d = new Date(date);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}T00:00:00`;
  }
  
  formatUtcToLocal(
    value: any,
    pattern: string = 'DD-MM-YYYY hh:mm:ss A',
  ): string {
    if (!value) return '';

    const d = dayjs.utc(value).local(); // convert UTC → local browser timezone
    return d.isValid() ? d.format(pattern) : '';
  }

  getBrowserTimezoneOffset(): { timezone: string; offset: string } {
    const timezoneName = dayjs.tz.guess(); // Detect browser timezone
    dayjs.tz.setDefault(timezoneName); // Optional: set globally
    const offsetMinutes = dayjs().tz(timezoneName).utcOffset(); // offset in minutes
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const formattedOffset = `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;

    return {
      timezone: timezoneName,
      offset: formattedOffset,
    };
  }
}
